import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Portal } from '@nula/core-ui';
import { EntityPreviewCard } from '@/components/entity-preview-card/entity-preview-card';
import { useEntityPreview } from '@/hooks/use-entity-preview';
import { useNavigateToEntity } from '@/hooks/navigation';
import { extractDocumentId } from '@/lib/documents/extract-document-id';
import styles from './nula-link.module.css';

export interface EditorEntityLinkPreviewProps {
  editor: Editor;
}

/**
 * Hover preview for plain document links (`nula://document/<id>` hrefs) in the
 * editor — e.g. agent-generated references, which render as ordinary link marks
 * (not nulaLink chips). Ports the legacy Lexical EntityLinkPreviewPlugin to
 * Tiptap, reusing the same EntityPreviewCard + useEntityPreview machinery the
 * `[[` nula-link chip uses, so hover behavior is identical.
 *
 * Listens for mouseover/out on the editor DOM (delegated — one pair of
 * listeners, not per-link), resolves the hovered link's documentId from its
 * href, and renders the preview card. LinkClickExtension tags these links with
 * data-link-type='document' for styling + as the hover target.
 */
export function EditorEntityLinkPreview({ editor }: EditorEntityLinkPreviewProps) {
  // The document id of the currently-hovered link (null = nothing hovered).
  const [docId, setDocId] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    const root = editor.view.dom;

    const onOver = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a');
      if (!a) return;
      const id = extractDocumentId(a.getAttribute('href') ?? '');
      if (!id) return;
      const rect = a.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
      setDocId(id);
    };

    root.addEventListener('mouseover', onOver);
    return () => root.removeEventListener('mouseover', onOver);
  }, [editor]);

  if (!docId) return null;
  // Keyed by docId so useEntityPreview re-subscribes per hovered link.
  return <PreviewCard key={docId} documentId={docId} pos={pos} onClose={() => setDocId(null)} />;
}

function PreviewCard({
  documentId,
  pos,
  onClose,
}: {
  documentId: string;
  pos: { top: number; left: number };
  onClose: () => void;
}) {
  const navigateToEntity = useNavigateToEntity();
  const { onMouseEnter, onMouseLeave, onCardMouseEnter, onCardMouseLeave, previewData, isOpen, loading, close } =
    useEntityPreview(documentId);
  const ref = useRef<HTMLDivElement>(null);

  // Open immediately on mount (mouseover already fired) — reuse the hook's
  // hover state machine for linger/dismiss.
  useEffect(() => {
    onMouseEnter();
    return () => onMouseLeave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) onClose();
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <Portal>
      <div
        ref={ref}
        className={styles.previewCard}
        style={{ '--preview-top': `${pos.top}px`, '--preview-left': `${pos.left}px` } as React.CSSProperties}
        onMouseEnter={onCardMouseEnter}
        onMouseLeave={onCardMouseLeave}
      >
        <EntityPreviewCard
          data={previewData}
          loading={loading}
          onOpen={(id) => {
            navigateToEntity(id);
            close();
          }}
        />
      </div>
    </Portal>
  );
}
